import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink, CheckCircle2, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader, PrimaryBtn, OutlineBtn } from "@/components/site/shared";
import { StarPicker } from "@/components/site/StarPicker";

const track = (event) => { try { if (window.gtag) window.gtag("event", event); } catch (e) {} };
const inCls = "w-full h-12 px-4 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-[var(--action)]";

export default function ReviewRequest() {
  const { token } = useParams();
  const [state, setState] = useState("loading"); // loading | open | done | invalid | submitted | expired
  const [ctx, setCtx] = useState({});
  const [f, setF] = useState({ reviewer_name: "", rating: 0, review_text: "" });
  const [busy, setBusy] = useState(false);
  const [googleUrl, setGoogleUrl] = useState("");
  const startRef = useRef(Date.now());

  useEffect(() => {
    api.get(`/reviews/request/${token}`).then((r) => {
      const s = r.data.status;
      if (s === "open") {
        setCtx(r.data);
        setF((p) => ({ ...p, reviewer_name: r.data.customer_first_name || "" }));
        setState("open");
        track("review_form_opened");
      } else { setState(s); }
    }).catch(() => setState("invalid"));
  }, [token]);

  const upd = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { toast.error("Please select a star rating."); return; }
    if (!f.review_text) { toast.error("Please write your review."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/reviews/request/${token}`, f);
      track("review_submitted");
      setGoogleUrl(r.data.google_leave_url || "");
      setState("done");
    } catch (err) { toast.error(err.response?.data?.detail || "Please try again."); }
    finally { setBusy(false); }
  };

  if (state === "loading") return <div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[var(--action)]" /></div>;

  if (state === "invalid" || state === "expired" || state === "submitted") {
    const msg = state === "submitted" ? "This review has already been submitted. Thank you!"
      : state === "expired" ? "This review link has expired." : "This review link is invalid.";
    return (
      <div>
        <PageHeader overline="Reviews" title="Review Link" />
        <section className="section-pad"><div className="max-w-xl mx-auto px-4 text-center" data-testid="review-link-state">
          <p className="text-[var(--text-muted)]">{msg}</p>
          <div className="mt-6"><OutlineBtn to="/">Back to Home</OutlineBtn></div>
        </div></section>
      </div>
    );
  }

  if (state === "done") {
    return (
      <div>
        <PageHeader overline="Thank You" title="Thank You!" subtitle="We appreciate you taking the time to share your experience." />
        <section className="section-pad"><div className="max-w-xl mx-auto px-4 text-center" data-testid="request-thank-you">
          <CheckCircle2 className="w-14 h-14 text-[var(--aqua)] mx-auto" />
          {googleUrl && (
            <>
              <p className="mt-6 font-semibold text-[var(--navy)]">Would you also like to share your experience on Google?</p>
              <div className="flex flex-wrap justify-center gap-3 mt-4">
                <PrimaryBtn href={googleUrl} target="_blank" rel="noopener noreferrer" onClick={() => track("google_review_clicked")} data-testid="request-google"><ExternalLink className="w-4 h-4" /> Review Us on Google</PrimaryBtn>
                <OutlineBtn to="/">Done</OutlineBtn>
              </div>
            </>
          )}
          {!googleUrl && <div className="mt-6"><OutlineBtn to="/">Done</OutlineBtn></div>}
        </div></section>
      </div>
    );
  }

  return (
    <div>
      <PageHeader overline="Your Experience" title="Share Your Experience"
        subtitle={ctx.service_type ? `How was your ${ctx.service_type}?` : "We'd love your feedback about your recent service."} />
      <section className="section-pad">
        <form onSubmit={submit} className="max-w-2xl mx-auto px-4 space-y-5 bg-white border border-gray-100 rounded-2xl p-6 sm:p-8 card-shadow" data-testid="review-request-form">
          <div>
            <label className="block text-sm font-semibold text-[var(--navy)] mb-2">Your Rating</label>
            <StarPicker value={f.rating} onChange={(v) => upd("rating", v)} />
          </div>
          <input className={inCls} placeholder="Your name" value={f.reviewer_name} onChange={(e) => upd("reviewer_name", e.target.value)} data-testid="req-name" />
          <textarea className={`${inCls} h-auto py-3`} rows={5} placeholder="Share your experience…" value={f.review_text} onChange={(e) => upd("review_text", e.target.value)} data-testid="req-text" />
          <button type="submit" disabled={busy} data-testid="submit-request-review"
            className="btn-lift bg-[var(--red)] hover:bg-[var(--red-dark)] text-white font-semibold rounded-full px-8 h-12 disabled:opacity-60">
            {busy ? "Submitting…" : "Submit Review"}
          </button>
        </form>
      </section>
    </div>
  );
}
